import { PLAYER_COLORS } from "./BoardView";
import { PICTURES, type PictureId } from "./pictures";
import { layout, soloLayout } from "./shared";
import { stageAt } from "./stages";
import { animalMask, animalOf, animalRatio } from "./animals";
import { TILE_BASE, tileKind } from "./tiles";

/**
 * نقشِ کوچک — بی فلش، فقط خانه‌های نقش. stage: مرحله‌ی آفلاین، تک‌رنگ (همان رنگِ تیره‌ی فلش‌ها). pic: نقشِ اتاقِ
 * دوستان، هر برش به رنگِ یک بازیکن تا پیدا باشد سهم‌ها چطورند. هر ردیف تکه‌تکه (پشتِ هم خانه‌های یک رنگ) یک مسیر
 * می‌شود، تا صدها خانه یک گره‌ی DOM نشوند.
 *
 * tiled: نقشِ تمام‌شده، کاشی‌کاری‌شده با همان رنگ‌های تخته (tiles.ts) — آلبوم و مرحله‌های برده در نوار.
 */
const INK = "#2a2440";
const cache = new Map<string, { n: number; h?: number; paths: string[]; colors: string[] }>();

/** خانه‌های نقش به رنگِ کاشی: هر ردیف تکه‌هایی از خانه‌های یک نوع، هر نوع یک مسیر. */
function tiledPaths(w: number, h: number, on: (c: number) => boolean) {
  const paths = ["", "", "", ""];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w;) {
      const k = tileKind(x, y, w, h);
      let e = x + 1;
      while (e < w && on(y * w + e) === on(y * w + x) && tileKind(e, y, w, h) === k) e++;
      if (on(y * w + x)) paths[k] += `M${x} ${y}h${e - x}v1h${x - e}z`;
      x = e;
    }
  }
  return { paths, colors: [...TILE_BASE] };
}

/** طرحِ جانور (animals.ts) روی شبکه‌ی ۳۲ ستونه، تک‌رنگ. */
function beast(id: string, tiled = false) {
  const key = `a${id}${tiled ? "t" : ""}`;
  let v = cache.get(key);
  if (v) return v;
  const a = animalOf(id)!;
  const cols = 32, rows = Math.max(1, Math.round(cols / animalRatio(a)));
  const ok = animalMask(a, cols, rows);
  if (tiled) {
    v = { n: cols, h: rows, ...tiledPaths(cols, rows, (c) => !!ok[c]) };
    cache.set(key, v);
    return v;
  }
  let d = "";
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols;) {
      let e = x + 1;
      while (e < cols && ok[y * cols + e] === ok[y * cols + x]) e++;
      if (ok[y * cols + x]) d += `M${x} ${y}h${e - x}v1h${x - e}z`;
      x = e;
    }
  }
  v = { n: cols, h: rows, paths: [d], colors: [INK] };
  cache.set(key, v);
  return v;
}

function shape(stage: number | undefined, pic: PictureId | undefined, tiled = false) {
  const key = (stage !== undefined ? `s${stage}` : `p${pic}`) + (tiled ? "t" : "");
  let v = cache.get(key);
  if (v) return v;
  const L = stage !== undefined ? soloLayout(stageAt(stage)) : layout(PICTURES[pic!], 90);
  const { n, reg } = L;
  if (tiled) {
    v = { n, ...tiledPaths(n, n, (c) => reg[c]! >= 0) };
    cache.set(key, v);
    return v;
  }
  const parts = stage !== undefined ? 1 : L.maps.length;
  const paths = Array.from({ length: parts }, () => "");
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n;) {
      const r = reg[y * n + x]!;
      let e = x + 1;
      while (e < n && (reg[y * n + e]! === r || (parts === 1 && r >= 0 && reg[y * n + e]! >= 0))) e++;
      if (r >= 0) paths[parts === 1 ? 0 : r] += `M${x} ${y}h${e - x}v1h${x - e}z`;
      x = e;
    }
  }
  v = { n, paths, colors: parts === 1 ? [INK] : [...PLAYER_COLORS] };
  cache.set(key, v);
  return v;
}

export function Thumb({ stage, pic, animal, tiled, className }: {
  stage?: number; pic?: PictureId; animal?: string; tiled?: boolean; className?: string;
}) {
  const { n, h = n, paths, colors } = animal ? beast(animal, tiled) : shape(stage, pic, tiled);
  // جانورِ بلند یا پهن وسطِ مربع
  const side = Math.max(n, h);
  return (
    <svg className={"ar-thumb" + (className ? " " + className : "")} viewBox={`${-1 - (side - n) / 2} ${-1 - (side - h) / 2} ${side + 2} ${side + 2}`} aria-hidden>
      {paths.map((d, k) => <path key={k} d={d} fill={colors[k]} />)}
    </svg>
  );
}
